/**
 * Louisiana State Configuration
 *
 * Provides default values for all Louisiana cities to reduce duplication.
 * Cities inherit these values and can override any property.
 *
 * Usage:
 *   import { louisianaStateConfig } from '../state.config';
 *   const lakeCharlesConfig = { ...louisianaStateConfig, ...lakeCharlesOverrides };
 */

export interface StateConfig {
  // State identity
  state: {
    name: string;
    abbreviation: string;
    code: string; // Lowercase for URLs (e.g., 'la')
  };

  // Default branding
  branding: {
    primaryColor: string;
    accentColor: string;
    logoPath?: string;
  };

  // Default contact
  contact: {
    supportEmail: string;
    salesEmail: string;
  };

  // Default timezone
  timezone: string;

  // Default deployment settings
  deployment: {
    cloudflareAccount: string;
    stateDomain: string; // e.g., 'la.toolboxlogic.com'
  };

  // Default database settings
  database: {
    schemaPrefix: string; // e.g., 'community_app_louisiana'
  };
}

export const louisianaStateConfig: StateConfig = {
  // State identity
  state: {
    name: 'Louisiana',
    abbreviation: 'LA',
    code: 'la',
  },

  // Louisiana branding defaults (matches Lake Charles legacy styling)
  branding: {
    primaryColor: '#1e40af', // Blue (consistent with platform)
    accentColor: '#0d9488', // Teal
    logoPath: '/images/toolboxlogic-logo.svg',
  },

  // Louisiana contact defaults
  contact: {
    supportEmail: '', // 'support@toolboxlogic.com',
    salesEmail: '', // 'sales@toolboxlogic.com',
  },

  // Central Time Zone
  timezone: 'America/Chicago',

  // Louisiana deployment defaults
  deployment: {
    cloudflareAccount: 'toolboxlogic',
    stateDomain: 'la.toolboxlogic.com',
  },

  // Louisiana database defaults
  database: {
    schemaPrefix: 'community_app_louisiana',
  },
};

/**
 * Helper function to merge city config with state defaults
 *
 * Usage:
 *   const lakeCharlesConfig = mergeStateConfig(louisianaStateConfig, lakeCharlesOverrides);
 */
export function mergeStateConfig(stateConfig: StateConfig, cityOverrides: Partial<any>): any {
  return {
    // State defaults
    state: stateConfig.state,
    timezone: stateConfig.timezone,

    // Merge branding (city can override colors)
    branding: {
      ...stateConfig.branding,
      ...(cityOverrides.branding || {}),
    },

    // Merge contact (city can override emails)
    contact: {
      ...stateConfig.contact,
      ...(cityOverrides.contact || {}),
    },

    // Merge deployment
    deployment: {
      ...stateConfig.deployment,
      ...(cityOverrides.deployment || {}),
    },

    // Merge database
    database: {
      ...stateConfig.database,
      ...(cityOverrides.database || {}),
    },

    // City-specific overrides
    ...cityOverrides,
  };
}
